'use client';

import { useState, useTransition, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams, useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import emailSpellChecker from '@zootools/email-spell-checker';
import { z } from 'zod';
import { metaPixelEvents, initPixelWithMatching, generateEventId } from '@/lib/metaPixel';
import { getStoredAttribution } from '@/lib/utm';
import { PromptQuestion } from '@/components/prompts/question';
import { Input } from '@shared/components/shadcn/input';
import { Label } from '@shared/components/shadcn/label';
import Required from '@/components/prompts/required';
// Actions moved to REST API
import { Button } from '@shared/components/shadcn/button';
import { Checkbox } from '@shared/components/shadcn/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@shared/components/shadcn/form';
import { Alert, AlertDescription } from '@shared/components/shadcn/alert';
import { AlertCircle } from 'lucide-react';
import { Prompt } from '@/types/assessment.types';
import { useIsEU } from '@/hooks/useIsEU';
import { useAssessmentStore } from './hooks/useAssessmentStore';
import { useAssessmentStore as useGlobalStore } from './stores/assessmentStore';
import { RealTimeAssessmentResults } from './components/results/RealTimeAssessmentResults';
import { captureException, captureFormError } from '@/lib/posthog-tracking';
import { normalizePostHogEmail } from '@/lib/posthog-identity';

// Reusable Error Alert Component
function ErrorAlert({ message }: { message: string }) {
  return (
    <div className="max-w-sm !mt-4">
      <Alert
        variant="destructive"
        className="h-auto bg-red-50 border-red-200 !px-4 py-3 rounded-lg"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <AlertDescription className="text-red-700 text-sm leading-relaxed">
            {message}
          </AlertDescription>
        </div>
      </Alert>
    </div>
  );
}

type Props = {
  prompt: Prompt;
  assessmentType: string;
  onResultsShown?: (shown: boolean) => void;
};

export default function PromptAnswerWithEmail({ prompt, assessmentType, onResultsShown }: Props) {
  const isEU = useIsEU();
  const searchParams = useSearchParams();
  const [showResults, setShowResults] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [publicAccessToken, setPublicAccessToken] = useState<string | null>(null);

  // Check for test mode (only in development)
  const isTestMode = process.env.NODE_ENV === 'development' && searchParams.get('test') === 'true';

  console.log('🏠 PromptAnswerWithEmail component loaded:', {
    assessmentType,
    showResults,
    runId,
    publicAccessToken,
    isTestMode,
  });
  type FormValues = {
    name: string;
    email: string;
    subscribe: boolean;
    [key: string]: any;
  };

  const form = useForm<FormValues>({
    defaultValues: {
      subscribe: !isEU,
      name: '',
      email: '',
    },
  });

  // useIsEU returns false during SSR, so reset subscribe for EU users after hydration
  useEffect(() => {
    if (isEU) form.setValue('subscribe', false);
  }, [isEU, form]);

  const [isPending, startTransition] = useTransition();
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [confirmedTypo, setConfirmedTypo] = useState(false);
  const router = useRouter();
  const store = useAssessmentStore(assessmentType);
  const { saveCompletedAssessment, getCompletedAssessment } = useGlobalStore();

  // Notify parent when results are shown
  useEffect(() => {
    if (onResultsShown) {
      onResultsShown(showResults);
    }
  }, [showResults, onResultsShown]);

  // Check for completed assessment on mount
  useEffect(() => {
    const completed = getCompletedAssessment(assessmentType);
    if (completed && !showResults) {
      setRunId(completed.runId);
      setPublicAccessToken(completed.publicAccessToken);
      setShowResults(true);
    }
  }, [assessmentType, getCompletedAssessment]);

  const onSubmit = (values: FormValues) => {
    // Block submission if there's an unconfirmed email suggestion
    if (emailSuggestion && !confirmedTypo) {
      form.setError('email', {
        type: 'manual',
        message: 'Please correct the email typo or confirm you want to keep it',
      });
      return;
    }

    // Check for blocking checkboxes in prompt questions
    for (let index = 0; index < prompt.questions.length; index++) {
      const question = prompt.questions[index];
      if (
        question.type === 'checkbox' &&
        question.typeOptions?.checkbox?.blockSubmissionIfNotChecked &&
        question.required
      ) {
        const checkboxAnswer = values[`question-${index}-${question.type}`];
        const isChecked = Array.isArray(checkboxAnswer) && checkboxAnswer.length > 0;

        if (!isChecked) {
          const errorMessage = 'You must agree to this to continue';
          form.setError(`question-${index}-${question.type}`, {
            type: 'required',
            message: errorMessage,
          });

          // Track form validation error
          captureFormError('assessment_submission', `question-${index}`, errorMessage, {
            assessmentType,
            userEmail: values.email,
            questionId: `question-${index}`,
          });

          return; // Block submission
        }
      }
    }

    // Capture current URL and tracking properties
    const currentUrl = window.location.href;

    // Advanced Matching - Initialize pixel with user data for 85%+ match rate
    initPixelWithMatching(values.email, values.name);

    // Track assessment submission with PostHog
    const analyticsEmail = normalizePostHogEmail(values.email);
    if (analyticsEmail) {
      posthog.identify(analyticsEmail, {
        email: analyticsEmail,
        name: values.name,
      });
    }

    // Persist original landing UTMs as person properties
    const utm = getStoredAttribution();
    if (utm) {
      posthog.setPersonProperties(
        {},
        {
          initial_utm_source: utm.utm_source,
          initial_utm_medium: utm.utm_medium,
          initial_utm_campaign: utm.utm_campaign,
          initial_utm_content: utm.utm_content,
          initial_utm_term: utm.utm_term,
          initial_referrer_domain: utm.referrer_domain,
        }
      );
    }

    posthog.capture('minitools_assessment_submitted', {
      minitools_assessment_type: assessmentType,
      minitools_assessment_url: currentUrl,
      minitools_subscribe: values.subscribe,
      minitools_is_eu: isEU,
    });

    // Track Lead event - Meta Pixel (CRITICAL for algorithm learning)
    const eventId = generateEventId();
    metaPixelEvents.trackLead(assessmentType, eventId);

    // Save answers to store for results page
    const allAnswers: Record<string, any> = {};
    prompt.questions.forEach((q, index) => {
      const key = `question-${index}-${q.type}`;
      allAnswers[key] = values[key];
    });

    store.setAnswers(allAnswers);
    store.setEmail(values.email);
    store.setName(values.name);
    store.setSubscribe(values.subscribe);

    startTransition(async () => {
      const questions = prompt.questions.map((q, index) => ({
        ...q,
        answer: values[`question-${index}-${q.type}`] || '',
      }));
      const { name, email, subscribe } = values;

      let error;
      try {
        // Call REST API with base path
        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
        const apiEndpoint = isTestMode ? '/api/assessments/test-email' : '/api/assessments/submit';

        if (isTestMode) {
          console.log(
            '🧪 Using test email route - no email verification or Meta audience submission'
          );
        }

        const response = await fetch(`${basePath}${apiEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            email,
            assessmentType,
            subscribe,
            questions: questions.map((q) => ({
              question: q.question,
              answer: q.answer || '',
            })),
            // For test-email route, we need to format answers differently
            ...(isTestMode && {
              answers: questions.map((q) => ({
                question: q.question,
                answer: q.answer || '',
              })),
            }),
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          error = result.error || 'Failed to submit assessment';
        } else {
          error = null;
          // Store TriggerDev data and immediately show results
          if (result.runId && result.publicAccessToken) {
            // Save to global store for back navigation
            saveCompletedAssessment({
              runId: result.runId,
              publicAccessToken: result.publicAccessToken,
              email: values.email,
              assessmentType,
              timestamp: Date.now(),
            });

            setRunId(result.runId);
            setPublicAccessToken(result.publicAccessToken);
            // Clear saved progress and show results immediately
            store.clearProgress();
            setShowResults(true);
          }
        }
      } catch (err) {
        error = err instanceof Error ? err.message : 'Something went wrong';

        // Track assessment submission error
        captureException(err, {
          source: 'assessment_submission',
          severity: 'high',
          assessmentType,
          userEmail: email,
          additionalData: {
            questions_count: questions.length,
            has_name: !!name,
            has_subscribe: subscribe,
            error_message: error,
          },
        });
      }

      if (error) {
        console.error('Error processing prompt:', error);
      }
    });
  };

  useEffect(() => {
    const email = form.watch('email');
    if (email) {
      // Debounce spell check to avoid running on every keystroke
      const timeoutId = setTimeout(() => {
        const suggestion = emailSpellChecker.run({ email });
        if (suggestion) {
          setEmailSuggestion(suggestion.full);
          setConfirmedTypo(false); // Reset confirmation when suggestion changes
        } else {
          setEmailSuggestion(null);
          setConfirmedTypo(false);
        }
      }, 500); // Wait 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    } else {
      // Clear suggestions if email is empty
      setEmailSuggestion(null);
      setConfirmedTypo(false);
    }
  }, [form.watch('email')]);

  const applySuggestion = () => {
    if (emailSuggestion) {
      form.setValue('email', emailSuggestion);
      setEmailSuggestion(null);
    }
  };

  // Show results after successful submission
  if (showResults && runId && publicAccessToken) {
    console.log('✅ Showing RealTimeAssessmentResults with TriggerDev');
    return (
      <RealTimeAssessmentResults
        runId={runId}
        publicAccessToken={publicAccessToken}
        email={form.getValues('email')}
        assessmentType={assessmentType}
        assessmentTitle={prompt.title}
      />
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {isTestMode && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded-md">
            <p className="text-sm text-yellow-800 font-medium">
              🧪 Test Mode Active - Email verification and Meta audience submission are disabled
            </p>
          </div>
        )}
        <div className="flex flex-col gap-8">
          {prompt.questions?.map((q, k) => (
            <PromptQuestion key={`${k}-${q.type}`} question={q} index={k} />
          ))}
          <div className="h-px bg-gray-200 my-8"></div>
          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              rules={{ required: true }}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-xs">
                  <FormLabel>
                    <Label className="mb-2 font-semibold flex items-center">
                      Name
                      <Required />
                    </Label>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  {error && <ErrorAlert message={error.message || 'This field is required'} />}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              rules={{
                required: 'Email is required',
                validate: (value) => {
                  const emailSchema = z.string().email();
                  const result = emailSchema.safeParse(value);
                  if (!result.success) {
                    return 'Please enter a valid email address';
                  }
                  return true;
                },
              }}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="mt-4 max-w-xs">
                  <FormLabel>
                    <Label className="mb-2 font-semibold flex items-center">
                      Email
                      <Required />
                    </Label>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  {emailSuggestion && (
                    <div className="mt-2">
                      <Alert
                        variant="default"
                        className="h-auto bg-[#BAC9DB]/20 !px-4 py-3 border-[#BAC9DB]"
                      >
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-4 w-4 !relative !inset-0 text-[#2A6354] mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <AlertDescription className="text-gray-700 mb-2">
                              Did you mean{' '}
                              <strong className="text-[#2A6354]">{emailSuggestion}</strong>?{' '}
                              <Button
                                variant="link"
                                className="p-0 h-auto underline text-[#2A6354] hover:text-[#2A6354]/80"
                                onClick={applySuggestion}
                              >
                                Update
                              </Button>
                            </AlertDescription>
                            <div className="flex items-start gap-2 mt-2">
                              <Checkbox
                                id="confirm-typo"
                                checked={confirmedTypo}
                                onCheckedChange={(checked) => {
                                  setConfirmedTypo(checked === true);
                                  if (checked) {
                                    form.clearErrors('email');
                                  }
                                }}
                                className="data-[state=checked]:bg-[#2A6354] data-[state=checked]:border-[#2A6354] border-2 border-[#BFBCC0] w-5 h-5 sm:w-4 sm:h-4"
                              />
                              <Label
                                htmlFor="confirm-typo"
                                className="font-normal cursor-pointer text-sm leading-relaxed text-gray-700"
                              >
                                No, I want to keep <strong>{field.value}</strong> as my email
                                address
                              </Label>
                            </div>
                          </div>
                        </div>
                      </Alert>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mt-1">
                    We&apos;ll send your personalized report to this email address.
                  </p>
                  {error && <ErrorAlert message={error.message || 'This field is required'} />}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="subscribe"
              rules={{
                validate: (value) => {
                  if (isEU && !value) {
                    return 'You must consent to receive the results to continue';
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormItem className="flex flex-col gap-2 mt-2">
                  <div className="flex items-start sm:items-end gap-2">
                    <FormControl>
                      <Checkbox
                        checked={value}
                        onCheckedChange={(checked) => {
                          onChange(checked);
                          onBlur();
                        }}
                        id="subscribe"
                      />
                    </FormControl>
                    <FormLabel>
                      <Label className="font-normal cursor-pointer" htmlFor="subscribe">
                        Yes — send me my personalised report + tutorials, and Lifehack&apos;s
                        ongoing productivity newsletter (unsubscribe anytime).{' '}
                        {isEU && <Required />}
                      </Label>
                    </FormLabel>
                  </div>
                  {isEU && error && (
                    <ErrorAlert message={error.message || 'This field is required'} />
                  )}
                </FormItem>
              )}
            />
            <Button
              disabled={isPending}
              type="submit"
              className="mt-8 max-w-max text-white font-semibold transition-colors"
              style={{ backgroundColor: '#2A6354' }}
              onMouseEnter={(e) =>
                !isPending && (e.currentTarget.style.backgroundColor = '#1f4a3d')
              }
              onMouseLeave={(e) =>
                !isPending && (e.currentTarget.style.backgroundColor = '#2A6354')
              }
            >
              {isPending ? (
                <div className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                'Get My Personalized Report'
              )}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
